import { useMemo } from "react";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { useRelatorio } from "@/components/escala/useRelatorio";
import { distintos, maisFrequente, plural } from "@/components/escala/relatorio";
import { formatDataBR, relTransferencias, rotuloLoja, type Loja, type LinhaTransferencia } from "@/api/escala.service";


const porDataENome = (a: LinhaTransferencia, b: LinhaTransferencia) =>
  a.data_transferencia.localeCompare(b.data_transferencia) || a.nome.localeCompare(b.nome);

function indicadoresTransferencias(linhas: LinhaTransferencia[], lojas: Loja[]): [Indicador, Indicador, Indicador, Indicador] {
  const maisSaidas = maisFrequente(linhas, (l) => l.loja_anterior_id);
  const maisEntradas = maisFrequente(linhas, (l) => l.loja_atual_id);
  const colaboradores = distintos(linhas, (l) => l.colaborador_id);
  const envolvidas = new Set(linhas.flatMap((l) => [l.loja_anterior_id, l.loja_atual_id])).size;
  return [
    { titulo: "Transferências", valor: linhas.length, nota: plural(colaboradores, "colaborador transferido", "colaboradores transferidos") },
    { titulo: "Lojas envolvidas", valor: envolvidas, nota: `de ${plural(lojas.length, "loja")}` },
    {
      titulo: "Loja com mais saídas",
      valor: maisSaidas ? maisSaidas[1] : "—",
      nota: maisSaidas ? rotuloLoja(lojas, maisSaidas[0]) : "nenhuma transferência no mês",
    },
    {
      titulo: "Loja com mais entradas",
      valor: maisEntradas ? maisEntradas[1] : "—",
      nota: maisEntradas ? rotuloLoja(lojas, maisEntradas[0]) : "nenhuma transferência no mês",
    },
  ];
}

// Mudanças de loja do mês, com os dias trabalhados em cada uma das duas lojas.
export function EscalaRelTransferenciasTab() {
  const r = useRelatorio({ buscar: relTransferencias, ordenar: porDataENome, mensagemErro: "Erro ao carregar o relatório de transferências" });
  const indicadores = useMemo(() => indicadoresTransferencias(r.linhas, r.lojas), [r.linhas, r.lojas]);

  const columns: Column<LinhaTransferencia>[] = [
    { key: "matricula", label: "Matrícula" },
    { key: "nome", label: "Nome" },
    { key: "data_transferencia", label: "Data", render: (l) => formatDataBR(l.data_transferencia), exportValue: (l) => formatDataBR(l.data_transferencia) },
    {
      key: "loja_anterior_id",
      label: "Loja Anterior",
      render: (l) => rotuloLoja(r.lojas, l.loja_anterior_id),
      exportValue: (l) => rotuloLoja(r.lojas, l.loja_anterior_id),
    },
    {
      key: "loja_atual_id",
      label: "Loja Atual",
      render: (l) => rotuloLoja(r.lojas, l.loja_atual_id),
      exportValue: (l) => rotuloLoja(r.lojas, l.loja_atual_id),
    },
    { key: "dias_loja_anterior", label: "Dias na Anterior" },
    { key: "dias_loja_atual", label: "Dias na Atual" },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} comCargo={false} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "transferencias", aba: "Transferências" }}
      competencia={r.filtros}
    >
      <DataTable columns={columns} {...r.tabela} />
    </RelatorioLayout>
  );
}
